import type {
  IChannelPayloadPlaylistAssignment,
  IScheduleRootAssignment
} from '../models/player-registration-payload'
import type { TFrame, TPlaylist } from '../models/playlist'
import { Temporal } from '@js-temporal/polyfill'
import { PlaybackMode } from '../database/custom-types/playback-mode'
import { effectiveChannelPlaybackModeForPlayerRegistration } from './effectivePlaylistPlaybackMode'
import { operatorAssignedPlayerMasterPlaylistUrn, playlistUrnIsPlayerMasterPlaylist } from './playerMasterPlaylist'
import {
  effectiveFrameScheduleEndIsoExclusive,
  effectiveFrameScheduleStartIso,
  isCampaignActiveAt,
  isPlaylistAssignmentActiveForPlayback
} from './selectActiveCampaign'

function scheduleRootHasPlayablePlaylists(c: IScheduleRootAssignment): boolean {
  return (c.playlists ?? []).some(p => (p.frames?.length ?? 0) > 0)
}

/**
 * Later (max) schedule start among two boundaries. Null means open-ended (−∞).
 * Returns the original string of the later bound (date-only or full ISO preserved).
 */
function laterScheduleStart(a: string | null, b: string | null): string | null {
  if (a == null) {
    return b
  }
  if (b == null) {
    return a
  }
  const aIso = effectiveFrameScheduleStartIso(a)
  const bIso = effectiveFrameScheduleStartIso(b)
  if (aIso == null) {
    return b
  }
  if (bIso == null) {
    return a
  }
  const aMs = Temporal.Instant.from(aIso).epochMilliseconds
  const bMs = Temporal.Instant.from(bIso).epochMilliseconds
  return aMs >= bMs ? a : b
}

/**
 * Earlier (min) exclusive schedule end among two boundaries. Null means open-ended (+∞).
 * Returns the original string of the earlier bound.
 */
function earlierScheduleEnd(a: string | null, b: string | null): string | null {
  if (a == null) {
    return b
  }
  if (b == null) {
    return a
  }
  const aIso = effectiveFrameScheduleEndIsoExclusive(a)
  const bIso = effectiveFrameScheduleEndIsoExclusive(b)
  if (aIso == null) {
    return b
  }
  if (bIso == null) {
    return a
  }
  const aMs = Temporal.Instant.from(aIso).epochMilliseconds
  const bMs = Temporal.Instant.from(bIso).epochMilliseconds
  return aMs <= bMs ? a : b
}

/**
 * Intersect a playlist row’s flight window with its schedule-root (DL assignment) channel window.
 *
 * Multiplexed playback roots null out top-level {@link IScheduleRootAssignment.startDate}/`endDate`
 * after merge, and {@link resolvePlaybackSegments} / interval rebuild only honor playlist-row dates.
 * Without this stamp, a DL “until” window is lost after combine and expired Demo C (etc.) stays in the
 * queue until a deferred registration campaign swap — which can leave playback stuck on another DL.
 */
function clampPlaylistAssignmentToChannelWindow(
  pl: IChannelPayloadPlaylistAssignment,
  channelStartDate: string | null,
  channelEndDate: string | null
): IChannelPayloadPlaylistAssignment {
  if (channelStartDate == null && channelEndDate == null) {
    return pl
  }
  const startDate = laterScheduleStart(pl.startDate ?? null, channelStartDate)
  const endDate = earlierScheduleEnd(pl.endDate ?? null, channelEndDate)
  if (startDate === (pl.startDate ?? null) && endDate === (pl.endDate ?? null)) {
    return pl
  }
  return { ...pl, startDate, endDate }
}

function stampChannelWindowOntoPlaylistRows(root: IScheduleRootAssignment): IScheduleRootAssignment {
  const channelStart = root.startDate ?? null
  const channelEnd = root.endDate ?? null
  if (channelStart == null && channelEnd == null) {
    return root
  }
  const playlists = (root.playlists ?? []).map(pl =>
    clampPlaylistAssignmentToChannelWindow(pl, channelStart, channelEnd)
  )
  return { ...root, playlists }
}

/**
 * Registration `schedule` order for playback: per-player master schedule root(s) first, then all
 * other roots in their registration array order (distribution-list resolution order from the API).
 */
function registrationScheduleRootsForPlayback(
  schedule: readonly IScheduleRootAssignment[]
): IScheduleRootAssignment[] {
  return schedule.filter(r => !playlistUrnIsPlayerMasterPlaylist(r.scheduleRootUrn))
}

export function orderRegistrationScheduleRootsForPlayback(
  schedule: readonly IScheduleRootAssignment[],
  masterPlaylistUrn?: string | null
): IScheduleRootAssignment[] {
  const operatorMaster = operatorAssignedPlayerMasterPlaylistUrn(masterPlaylistUrn)
  const masterLower = (operatorMaster?.trim() ?? '').toLowerCase()
  const filtered = registrationScheduleRootsForPlayback(schedule)
  if (masterLower === '') {
    return [...filtered]
  }
  const master: IScheduleRootAssignment[] = []
  const rest: IScheduleRootAssignment[] = []
  for (const root of filtered) {
    if (root.scheduleRootUrn.trim().toLowerCase() === masterLower) {
      master.push(root)
    }
    else {
      rest.push(root)
    }
  }
  return [...master, ...rest]
}

function mergeScheduleRootsIntoCombined(
  roots: readonly IScheduleRootAssignment[],
  operatorMasterPlaylistUrn?: string | null
): IScheduleRootAssignment {
  const primary = roots[0]!
  const masterLower = (operatorAssignedPlayerMasterPlaylistUrn(operatorMasterPlaylistUrn)?.trim() ?? '').toLowerCase()
  const playlists: IChannelPayloadPlaylistAssignment[] = []
  const embeddedPlaylistFrames: Record<string, TFrame[]> = {}
  const playlistDocs: Array<Pick<TPlaylist, 'playbackMode'>> = []
  let nextIndex = 0

  for (const root of roots) {
    const rootIsOperatorMaster = masterLower !== ''
      && root.scheduleRootUrn.trim().toLowerCase() === masterLower
    const channelStart = root.startDate ?? null
    const channelEnd = root.endDate ?? null
    for (const pl of root.playlists ?? []) {
      const plIsMasterUrn = masterLower !== ''
        && pl.playlistUrn.trim().toLowerCase() === masterLower
      const clamped = clampPlaylistAssignmentToChannelWindow(pl, channelStart, channelEnd)
      playlists.push({
        ...clamped,
        index: nextIndex++,
        /**
         * Stamp so {@link resolvePlaybackSegments} does not treat list-root rows that reuse the
         * master playlist URN as screen-master bypass candidates after merge (combined root URN
         * is the primary/master identity).
         */
        ...(plIsMasterUrn ? { operatorMasterTimeBlockBypass: rootIsOperatorMaster } : {})
      })
      playlistDocs.push({ playbackMode: undefined })
    }
    if (root.embeddedPlaylistFrames != null) {
      Object.assign(embeddedPlaylistFrames, root.embeddedPlaylistFrames)
    }
  }

  return {
    scheduleRootUrn: primary.scheduleRootUrn,
    title: primary.title,
    version: Math.max(...roots.map(r => r.version ?? 0)),
    description: primary.description ?? null,
    /**
     * Concatenated multi-root spine walks `playlists[]` in index order. Force Rotating so a
     * Random primary mode does not reshuffle rows contributed by other roots.
     * Drop `customSequence`: it addresses playlist slots within one root and cannot span a
     * multiplexed spine (single-root path returns the root as-is and keeps its sequence).
     */
    playbackMode: effectiveChannelPlaybackModeForPlayerRegistration(
      PlaybackMode.Rotating,
      playlistDocs
    ),
    customSequence: null,
    startDate: null,
    endDate: null,
    widgetConfig: primary.widgetConfig ?? null,
    playlists,
    ...(Object.keys(embeddedPlaylistFrames).length > 0 ? { embeddedPlaylistFrames } : {}),
    ...(primary.widgetAssetMetadata != null ? { widgetAssetMetadata: primary.widgetAssetMetadata } : {}),
    ...(primary.hlsVideoAssetMetadata != null ? { hlsVideoAssetMetadata: primary.hlsVideoAssetMetadata } : {})
  }
}

function filterActivePlaylistRows(
  root: IScheduleRootAssignment,
  referenceInstant: Temporal.Instant,
  operatorMasterPlaylistUrn?: string | null
): IScheduleRootAssignment {
  const playlists = (root.playlists ?? []).filter(pl =>
    isPlaylistAssignmentActiveForPlayback(pl, referenceInstant, operatorMasterPlaylistUrn, {
      scheduleRootUrn: root.scheduleRootUrn,
      skipGeoFilter: true
    })
  )
  return { ...root, playlists }
}

/**
 * Builds one playback schedule root from **all** registration schedule roots that are active at
 * `referenceInstant`, multiplexed in order: master playlist first (when `masterPlaylistUrn` is set),
 * then distribution-list roots in registration order. {@link resolvePlaybackSegments} walks the
 * combined `playlists[]` in index order (rotating / sequential / random per effective mode).
 *
 * Roots outside their channel date window are skipped. When none are active, returns `null`
 * (no legacy “play first root anyway” fallback — avoids expired designated-list / Book content).
 *
 * Each active root’s channel `startDate`/`endDate` (DL assignment flight) is intersected onto its
 * playlist rows before merge. The combined root clears top-level dates (no single channel window),
 * but stamped playlist dates keep interval rebuild / {@link scheduleFingerprint} honest after a
 * window ends without waiting for a deferred registration campaign swap.
 */
export function combineRegistrationScheduleRootsForPlayback(
  schedule: readonly IScheduleRootAssignment[],
  referenceInstant: Temporal.Instant,
  options?: { masterPlaylistUrn?: string | null }
): IScheduleRootAssignment | null {
  const registrationSchedule = registrationScheduleRootsForPlayback(schedule)
  if (registrationSchedule.length === 0) {
    return null
  }

  const ordered = orderRegistrationScheduleRootsForPlayback(registrationSchedule, options?.masterPlaylistUrn)
  const playable = ordered.filter(scheduleRootHasPlayablePlaylists)
  const searchPool = playable.length > 0 ? playable : [...ordered]

  const masterUrnOpt = options?.masterPlaylistUrn
  const pool = searchPool
    .filter(r => isCampaignActiveAt(r, referenceInstant))
    .map(r => filterActivePlaylistRows(r, referenceInstant, masterUrnOpt))
    .filter(r => (r.playlists?.length ?? 0) > 0 || playable.length === 0)

  if (pool.length === 0) {
    return null
  }

  if (pool.length === 1) {
    /** Preserve root `playbackMode` / `customSequence` when only one root is active. */
    const single = pool[0]!
    if ((single.playlists?.length ?? 0) === 0) {
      return null
    }
    /**
     * Stamp channel window onto playlist rows so interval {@link resolvePlaybackSegments} drops them
     * when the DL “until” elapses — root dates alone are ignored by the resolver.
     */
    return stampChannelWindowOntoPlaylistRows(single)
  }

  const merged = mergeScheduleRootsIntoCombined(pool, masterUrnOpt)
  return (merged.playlists?.length ?? 0) > 0 ? merged : null
}
